import React, { useState, useEffect } from 'react';

interface RequirementStatus {
  met: boolean;
  status?: string;
  totalShares?: number;
  approvedShares?: number;
  pendingShares?: number;
  message: string;
}

interface EligibilityData {
  isEligible: boolean;
  requirements: {
    kycCompleted: RequirementStatus;
    minimumShares: RequirementStatus;
  };
}

interface ReferralLinkRequirementsProps {
  userId: number;
  referralLink: string;
  onNavigateToKYC?: () => void;
  onNavigateToPurchase?: () => void;
}

const ReferralLinkRequirements: React.FC<ReferralLinkRequirementsProps> = ({
  userId,
  referralLink,
  onNavigateToKYC,
  onNavigateToPurchase
}) => {
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    // ALWAYS SHOW REFERRAL LINK - NO RESTRICTIONS
    setEligibility({
      isEligible: true,
      requirements: {
        kycCompleted: { met: true, message: 'KYC completed' },
        minimumShares: { met: true, message: 'Minimum shares met' }
      }
    });
    setLoading(false);
  }, [userId]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="text-center">
          <div className="text-3xl mb-2">🔗</div>
          <h3 className="text-white text-lg font-semibold mb-2">Referral Link</h3>
          <p className="text-white text-sm">Checking eligibility...</p>
        </div>
      </div>
    );
  }

  if (!eligibility) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="text-center">
          <div className="text-3xl mb-2">❌</div>
          <h3 className="text-white text-lg font-semibold mb-2">Error</h3>
          <p className="text-white text-sm">Unable to check referral link eligibility</p>
        </div>
      </div>
    );
  }

  const { isEligible, requirements } = eligibility;
  const { kycCompleted, minimumShares } = requirements;

  // If eligible, show the referral link prominently (SIMPLIFIED - Mobile Optimized)
  if (isEligible) {
    return (
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg p-3 border border-yellow-400 shadow-lg">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
          <div className="text-white text-sm font-mono break-all mb-2 bg-black/20 px-3 py-2 rounded">
            {referralLink}
          </div>
          <button
            onClick={copyToClipboard}
            className={`w-full py-2 px-4 rounded font-semibold text-sm transition-all ${
              copiedLink
                ? 'bg-green-600 text-white'
                : 'bg-white text-yellow-600 hover:bg-gray-100'
            }`}
          >
            {copiedLink ? '✓ Copied!' : '📋 Copy Link'}
          </button>
        </div>
      </div>
    );
  }

  // If not eligible, show requirements card
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="text-center mb-6">
        <div className="text-4xl mb-3">🔒</div>
        <h3 className="text-white text-xl font-bold mb-2">Unlock Your Referral Link</h3>
        <p className="text-white text-sm">
          Complete these requirements to start earning commissions
        </p>
      </div>

      <div className="space-y-4">
        {/* KYC Requirement */}
        <div className={`rounded-lg border-2 p-4 transition-all ${
          kycCompleted.met 
            ? 'bg-green-900/20 border-green-500/50' 
            : 'bg-yellow-900/20 border-yellow-500/50'
        }`}>
          <div className="flex items-start gap-3">
            <div className="text-3xl flex-shrink-0">
              {kycCompleted.met ? '✅' : '⏳'}
            </div>
            <div className="flex-1">
              <h4 className={`font-bold text-lg mb-1 ${
                kycCompleted.met ? 'text-green-400' : 'text-yellow-400'
              }`}>
                {kycCompleted.met ? 'KYC Completed' : 'Complete KYC Verification'}
              </h4>
              <p className="text-gray-300 text-sm mb-3">
                {kycCompleted.met 
                  ? 'Your identity verification is complete. You can start referring!' 
                  : 'Fill out your KYC form to verify your identity. Admin approval is not required - just complete the form.'}
              </p>
              {kycCompleted.status && (
                <div className="text-sm text-white mb-2">
                  Status: <span className="font-semibold capitalize">{kycCompleted.status}</span>
                </div>
              )}
              {!kycCompleted.met && onNavigateToKYC && (
                <button
                  onClick={onNavigateToKYC}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                >
                  Complete KYC Form →
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Share Ownership Requirement */}
        <div className={`rounded-lg border-2 p-4 transition-all ${
          minimumShares.met 
            ? 'bg-green-900/20 border-green-500/50' 
            : 'bg-yellow-900/20 border-yellow-500/50'
        }`}>
          <div className="flex items-start gap-3">
            <div className="text-3xl flex-shrink-0">
              {minimumShares.met ? '✅' : '⏳'}
            </div>
            <div className="flex-1">
              <h4 className={`font-bold text-lg mb-1 ${
                minimumShares.met ? 'text-green-400' : 'text-yellow-400'
              }`}>
                {minimumShares.met ? 'Share Owner' : 'Own At Least 1 Share'}
              </h4>
              <p className="text-gray-300 text-sm mb-3">
                {minimumShares.met 
                  ? `You own ${minimumShares.totalShares} share${minimumShares.totalShares !== 1 ? 's' : ''}. You're ready to refer!` 
                  : 'Purchase at least 1 share to unlock your referral link. Pending shares count towards this requirement.'}
              </p>
              {minimumShares.totalShares !== undefined && minimumShares.totalShares > 0 && (
                <div className="text-sm text-white mb-2 space-y-1">
                  <div>Total Shares: <span className="font-semibold text-white">{minimumShares.totalShares}</span></div>
                  {minimumShares.approvedShares !== undefined && minimumShares.approvedShares > 0 && (
                    <div>• Approved: <span className="font-semibold text-green-400">{minimumShares.approvedShares}</span></div>
                  )}
                  {minimumShares.pendingShares !== undefined && minimumShares.pendingShares > 0 && (
                    <div>• Pending: <span className="font-semibold text-yellow-400">{minimumShares.pendingShares}</span></div>
                  )}
                </div>
              )}
              {!minimumShares.met && onNavigateToPurchase && (
                <button
                  onClick={onNavigateToPurchase}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                >
                  Purchase Shares →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="mt-6 pt-6 border-t border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <span className="text-white">Progress</span>
          <span className="text-white font-semibold">
            {[kycCompleted.met, minimumShares.met].filter(Boolean).length} / 2 Complete
          </span>
        </div>
        <div className="mt-2 bg-gray-700 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-full transition-all duration-500"
            style={{ width: `${([kycCompleted.met, minimumShares.met].filter(Boolean).length / 2) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReferralLinkRequirements;

